/* Copyright (c) 2018-2025, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

import { Observable } from 'rxjs';
import { OpenTelemetrySpan } from 'distributed-tracing/lib/distributed-tracing';
import { JsEventsServerConfig } from '../config/js-events.config';

export enum ConsentLevel {
    Full = 'Full',
    None = 'None',
    Temp = 'Temp'
}

export enum GdprLevel {
    Required = 'required',
    Functional = 'functional',
    Technical = 'technical',
    Behavioral = 'behavioral'
}

export enum Personalization {
    Anonymous = 'anonymous',
    DeviceOnly = 'deviceOnly',
    DevicePreferred = 'devicePreferred',
    UserPreferred = 'userPreferred',
    UserRequired = 'userRequired'
}

export enum UnassignedStates {
    Undefined = 'undefined'
}

export enum EventsDbNames {
    EventsCommonStore = 'eventsCommonStore',
    EventsDetailStore = 'eventsDetailStore',
    EventsDetailStoreRequired = 'eventsDetailStoreRequired',
    EventsDetailStoreTechnical = 'eventsDetailStoreTechnical',
    EventsDetailStoreBehavioral = 'eventsDetailStoreBehavioral',
    UserDataConsentStore = 'userDataConsentStore'
}

export interface JsEventsCommonData {
    clientId?: string;
    clientVer?: string;
    eventSchemaVer?: string;
    eventSysVer?: string;
    eventProtocol?: string;
    deviceId?: string;
    userId?: string;
    externalUserId?: string;
    idpId?: string;
    sessionId?: string;
    sentTs?: string;
    events?: FormattedEventDetail[];
    gdprFuncOptIn?: string;
    gdprTechOptIn?: string;
    gdprBehOptIn?: string;
    deviceGdprFuncOptIn?: string;
    deviceGdprTechOptIn?: string;
    deviceGdprBehOptIn?: string;
    deviceOS?: string;
    deviceOSVersion?: string;
    deviceType?: string;
    deviceModel?: string;
    deviceMake?: string;
    clientType?: string;
    clientVariant?: string;
    integrationId?: string;
    browserType?: string;
    productName?: string;
    productVersion?: string;
    cpuArchitecture?: string;
}

export interface UserConsent {
    functional: ConsentLevel;
    technical: ConsentLevel;
    behavioral: ConsentLevel;
}

export interface DataCollectionConsent {
    required?: ConsentLevel;
    functional: ConsentLevel;
    technical: ConsentLevel;
    behavioral: ConsentLevel;
}

export interface UserConsentInfo {
    userId: string;
    externalUserId: string;
    idpId: string;
    userConsent: DataCollectionConsent;
    expiryTime?: string;
}

export interface JsEventsConfig {
    events: JsEventsServerConfig;
}

export interface RequestParams {
    sync?: boolean;
    platform?: boolean;
}

export type CallbackType = (param?: any) => void;

export type SendMethodType = (
    deviceConsent: DataCollectionConsent,
    userConsent: DataCollectionConsent,
    dbName: EventsDbNames,
    dbGdprType: GdprLevel,
    recordKey: string,
    testCommonData: CommonStoreData
) => Observable<any>;

export type SdkMethodType = (
    params: RequestParams,
    eventData: string,
    isImmediateRequest: boolean,
    parentSpan?: OpenTelemetrySpan
) => Observable<any>;

export interface EventMetadata {
    appExit?: boolean;
    negativeDurationLimit?: number;
    positiveDurationLimit?: number;
    afterDone?: CallbackType;
    immediateRequest?: boolean;
    anonymize?: boolean;
    useBeacon?: boolean;
}

export interface DataContext {
    commonData: JsEventsCommonData;
    events: FormattedEventDetail[];
    eventsToBeSent: number;
}

export interface CommonStoreData {
    common: JsEventsCommonData;
}

export interface EventsStoreData {
    events: FormattedEventDetail[];
}

export interface UserConsentStoreData {
    userConsent: DataCollectionConsent;
}

export interface RecordKey {
    userId?: string;
    sessionId?: string;
}

export interface FormattedEventDetail {
    name: string;
    ts: string;
    parameters: object;
    personalization?: Personalization;
}

export interface JsEventsConfigData {
    server: string;
    version: string;
}

export interface EventDataElements {
    commonData?: JsEventsCommonData;
    config?: JsEventsConfigData;
    telemetryEventIds?: {
        streamingProfileGuid: string;
        systemInfoGuid: string;
    };
}

export enum ExperimentActivationType {
    USER_ID = 'USER_ID',
    DEVICE_ID = 'DEVICE_ID',
    SESSION_ID = 'SESSION_ID'
}

export enum CloudVariableStorageType {
    SharedStorage = 'SharedStorage',
    LocalStorage = 'LocalStorage',
    AbHub = 'AbHub',
    IndexedDb = 'IndexedDb'
}

export interface RecordKeyDbStates {
    recordKey: string;
    functionalDbIsClear: boolean;
    technicalDbIsClear: boolean;
    behavioralDbIsClear: boolean;
    requiredDbIsClear: boolean;
}

export interface AllDbStates {
    commonDbIsClear: boolean;
    functionalDbIsClear: boolean;
    technicalDbIsClear: boolean;
    behavioralDbIsClear: boolean;
    requiredDbIsClear: boolean;
    wlsDbIsClear: boolean;
}
